export type MobileRunPhase = "queued" | "running" | "needs-input" | "succeeded" | "failed" | "cancelled"

export interface MobileRunState {
    version: 1
    nodeId: string
    workspaceId: string
    taskId: string
    title: string
    phase: MobileRunPhase
    startedAt?: string
    updatedAt: string
    sequence: number
}

export type MobileRunStateResult =
    | { status: "applied"; state: MobileRunState }
    | { status: "ignored"; reason: "stale" | "duplicate"; state: MobileRunState }
    | { status: "rejected"; reason: "identity-mismatch"; state: MobileRunState }

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value)
}

function requiredString(value: unknown, field: string): string {
    if (typeof value !== "string" || value.trim().length === 0) throw new Error(`Mobile run state is missing ${field}`)
    return value
}

function timestamp(value: unknown, field: string): string {
    const result = requiredString(value, field)
    if (!Number.isFinite(Date.parse(result))) throw new Error(`Mobile run state has an invalid ${field}`)
    return result
}

function sequence(value: unknown): number {
    if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 1) throw new Error("Mobile run state has an invalid sequence")
    return value
}

function isMobileRunPhase(value: unknown): value is MobileRunPhase {
    return value === "queued" || value === "running" || value === "needs-input" || value === "succeeded" || value === "failed" || value === "cancelled"
}

export function mobileRunPhaseFromStatus(status: string): MobileRunPhase | null {
    switch (status) {
        case "pending":
        case "queued":
        case "waiting":
        case "starting":
            return "queued"
        case "running":
            return "running"
        case "gated":
        case "parked":
        case "needs-input":
            return "needs-input"
        case "completed":
        case "done":
        case "succeeded":
            return "succeeded"
        case "error":
        case "failed":
        case "done_with_failures":
            return "failed"
        case "cancelled":
        case "stopped":
            return "cancelled"
        default:
            return null
    }
}

export function parseMobileRunState(value: unknown): MobileRunState {
    if (!isRecord(value)) throw new Error("Mobile run state must be an object")
    const allowedFields = new Set(["version", "nodeId", "workspaceId", "taskId", "title", "phase", "startedAt", "updatedAt", "sequence"])
    if (Object.keys(value).some((field) => !allowedFields.has(field))) throw new Error("Mobile run state contains unsupported fields")
    if (value.version !== 1) throw new Error("Mobile run state has an unsupported version")
    if (!isMobileRunPhase(value.phase)) throw new Error("Mobile run state has an invalid phase")

    return {
        version: 1,
        nodeId: requiredString(value.nodeId, "nodeId"),
        workspaceId: requiredString(value.workspaceId, "workspaceId"),
        taskId: requiredString(value.taskId, "taskId"),
        title: requiredString(value.title, "title"),
        phase: value.phase,
        ...(value.startedAt === undefined ? {} : { startedAt: timestamp(value.startedAt, "startedAt") }),
        updatedAt: timestamp(value.updatedAt, "updatedAt"),
        sequence: sequence(value.sequence),
    }
}

function sameIdentity(left: MobileRunState, right: MobileRunState): boolean {
    return left.nodeId === right.nodeId && left.workspaceId === right.workspaceId && left.taskId === right.taskId
}

function samePresentation(left: MobileRunState, right: MobileRunState): boolean {
    return left.phase === right.phase && left.title === right.title && left.startedAt === right.startedAt
}

export function applyMobileRunState(current: MobileRunState | null, candidate: MobileRunState): MobileRunStateResult {
    const next = parseMobileRunState(candidate)
    if (!current) return { status: "applied", state: next }
    const previous = parseMobileRunState(current)
    if (!sameIdentity(previous, next)) return { status: "rejected", reason: "identity-mismatch", state: previous }
    if (next.sequence <= previous.sequence) return { status: "ignored", reason: "stale", state: previous }
    if (samePresentation(previous, next)) return { status: "ignored", reason: "duplicate", state: previous }
    return { status: "applied", state: next }
}
